import { useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useLibrary } from "../../../../providers";
import { useRequestWithFeedback } from "../../../../composites";
import { useObjectMemo } from "../../../../hooks";

import { actions as userSavedSearchesActions } from "./slice";
import { customAlertAPI } from "./api";
import { selectAllSavedSearches } from "./selectors";

import type { NetworkPayload, OverrideRunProps } from "../../../../composites";
import type { CustomAlertPayload } from "../../../modules/alerts/custom";

export const useGetCustomAlerts = () => {
  const dispatch = useDispatch();
  const { send, loading } =
    useRequestWithFeedback<
      API.Paginated<"saved_searches", API.UserSavedSearch>
    >();
  const customAlerts = useSelector(selectAllSavedSearches);

  const fetchAdvancedAlerts = useCallback(
    (
      overrideRunProps: OverrideRunProps<
        API.Paginated<"saved_searches", API.UserSavedSearch>
      > = {},
    ) => {
      const { onData, ...restRunProps } = overrideRunProps;
      return send({
        action: customAlertAPI.getAlerts(),
        onData(data) {
          if (Array.isArray(data.saved_searches)) {
            dispatch(userSavedSearchesActions.setAll(data.saved_searches));
          }
          onData?.(data);
        },
        ...restRunProps,
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    send: fetchAdvancedAlerts,
    loading,
    customAlerts,
  });
};

export const useCreateCustomAlert = () => {
  const t = useLibrary("translations");
  const dispatch = useDispatch();
  const { send, loading } =
    useRequestWithFeedback<
      API.Paginated<"saved_searches", API.UserSavedSearch>
    >();

  const createCustomAlert = useCallback(
    (
      payload: CustomAlertPayload,
      {
        onSuccess,
        ...overrideRunProps
      }: OverrideRunProps<
        API.Paginated<"saved_searches", API.UserSavedSearch>
      > = {},
    ) => {
      return send({
        action: customAlertAPI.saveAlert(payload) as unknown as NetworkPayload,
        onSuccess(data) {
          if (
            Array.isArray(data.saved_searches) &&
            data.saved_searches.length > 0
          ) {
            dispatch(userSavedSearchesActions.addOne(data.saved_searches[0]));
          }
          onSuccess?.(data);
        },
        messaging: {
          toast: {
            success: t
              ? t.getString("alerts-toast-success-create", {}, "Alert saved.")
              : "Alert saved.",
            error: t
              ? t.getString(
                  "alerts-toast-error-create",
                  {},
                  "Could not save alert.",
                )
              : "Could not save alert.",
          },
        },
        ...overrideRunProps,
      });
    },
    [send, t, dispatch],
  );

  return useObjectMemo({
    loading,
    send: createCustomAlert,
  });
};

export const useDeleteCustomAlert = () => {
  const t = useLibrary("translations");
  const dispatch = useDispatch();
  const { send, loading } = useRequestWithFeedback<null>();

  const deleteCustomAlert = useCallback(
    (
      customAlertId: API.SavedSearchId,
      { onSuccess, onError, ...overrideRunProps }: OverrideRunProps<null> = {},
    ) => {
      return send({
        action: customAlertAPI.deleteAlert(customAlertId),
        onSuccess(data) {
          dispatch(userSavedSearchesActions.removeOne(customAlertId));
          onSuccess?.(data);
        },
        onError(err, setErrorMessage) {
          // If the server returns 404, the alert is already gone — remove
          // it from the local store so the user is not stuck with a phantom
          // alert that can never be deleted.
          if (err.response?.status === 404) {
            dispatch(userSavedSearchesActions.removeOne(customAlertId));
          }
          onError?.(err, setErrorMessage);
        },
        messaging: {
          toast: {
            success: t
              ? t.getString("alerts-toast-success-delete", {}, "Alert deleted.")
              : "Alert deleted.",
            error: t
              ? t.getString(
                  "alerts-toast-error-delete",
                  {},
                  "Could not delete alert.",
                )
              : "Could not delete alert.",
          },
        },
        ...overrideRunProps,
      });
    },
    [t, send, dispatch],
  );

  return useObjectMemo({
    loading,
    send: deleteCustomAlert,
  });
};
